/**
 * JV Buy the Look — toggles, size selection, total/installments and multi-add to cart.
 * Syncs the locked main product with Horizon `variant:update`.
 */
(function () {
  const CART_UPDATE = 'cart:update';
  const VARIANT_UPDATE = 'variant:update';

  /**
   * @param {HTMLElement | null} el
   * @param {unknown} fallback
   */
  function parseJson(el, fallback) {
    if (!el) return fallback;

    try {
      return JSON.parse(el.textContent || '');
    } catch {
      return fallback;
    }
  }

  /**
   * @param {number} cents
   * @param {string} currency
   */
  function formatMoney(cents, currency) {
    return (Number(cents) / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function getI18n(root) {
    return {
      item: root.dataset.i18nItem || 'item',
      items: root.dataset.i18nItems || 'itens',
      noInterest: root.dataset.i18nNoInterest || 'sem juros',
      selectOne: root.dataset.i18nSelectOne || 'Selecione ao menos 1 item',
      addCount: root.dataset.i18nAddCount || 'Adicionar __N__ à sacola',
      pairsWith: root.dataset.i18nPairsWith || 'Combina com',
      soldOut: root.dataset.i18nSoldOut || 'Esgotado',
    };
  }

  /**
   * @param {{ checked?: boolean; available?: boolean }[]} items
   */
  function getCount(items) {
    let count = 0;

    for (const item of items) {
      if (item.checked && item.available) count += 1;
    }

    return count;
  }

  /**
   * @param {{ checked?: boolean; available?: boolean; cents?: number }[]} items
   */
  function getTotal(items) {
    let total = 0;

    for (const item of items) {
      if (item.checked && item.available) total += Number(item.cents) || 0;
    }

    return total;
  }

  /**
   * @param {object} item
   * @param {string} value
   */
  function isSizeAvailable(item, value) {
    if (!item || item.sizeOptionIndex == null || !Array.isArray(item.variants)) return false;

    return item.variants.some(
      (variant) =>
        variant.available && String(variant.options[item.sizeOptionIndex]) === String(value)
    );
  }

  /**
   * @param {HTMLElement} root
   * @param {object[]} items
   * @param {{ loading?: boolean }} ui
   */
  function render(root, items, ui) {
    const currency = root.dataset.currency || 'BRL';
    const installments = Number(root.dataset.installments) || 6;
    const i18n = getI18n(root);
    const count = getCount(items);
    const total = getTotal(items);

    for (const row of root.querySelectorAll('[data-jv-look-row]')) {
      if (!(row instanceof HTMLElement)) continue;

      const index = Number(row.dataset.index);
      const item = items[index];

      if (!item) continue;

      const disabledLook = !item.available || (!item.locked && !item.checked);

      row.classList.toggle('is-on', !!item.checked);
      row.classList.toggle('is-dis', disabledLook);

      const toggle = row.querySelector('[data-jv-look-toggle]');

      if (toggle instanceof HTMLButtonElement) {
        toggle.classList.toggle('is-on', !!item.checked);
        toggle.classList.toggle('is-dis', !item.available);
        toggle.setAttribute('aria-pressed', item.checked ? 'true' : 'false');

        if (!item.locked) {
          toggle.disabled = !item.available;
        }
      }

      const badgeAvailable = row.querySelector('[data-jv-look-badge-available]');
      const badgeSoldOut = row.querySelector('[data-jv-look-badge-soldout]');

      if (badgeAvailable instanceof HTMLElement) {
        badgeAvailable.hidden = !item.available;
        if (item.available) badgeAvailable.textContent = i18n.pairsWith;
      }

      if (badgeSoldOut instanceof HTMLElement) {
        badgeSoldOut.hidden = !!item.available;
        if (!item.available) badgeSoldOut.textContent = i18n.soldOut;
      }

      for (const sizeBtn of row.querySelectorAll('[data-jv-look-size]')) {
        if (!(sizeBtn instanceof HTMLElement)) continue;

        const value = sizeBtn.dataset.jvSize || '';
        const unavailable = !isSizeAvailable(item, value);
        const selected = String(item.selectedSize) === String(value);

        sizeBtn.classList.toggle('is-on', selected);
        sizeBtn.classList.toggle('is-unavailable', unavailable);
        sizeBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        sizeBtn.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      }

      const priceEl = row.querySelector('[data-jv-look-price]');

      if (priceEl instanceof HTMLElement) {
        priceEl.textContent = formatMoney(item.cents, currency);
      }
    }

    const totalEl = root.querySelector('[data-jv-look-total]');

    if (totalEl instanceof HTMLElement) {
      totalEl.textContent = formatMoney(total, currency);
    }

    const parcEl = root.querySelector('[data-jv-look-parc]');

    if (parcEl instanceof HTMLElement) {
      if (count < 1) {
        parcEl.textContent = i18n.selectOne;
      } else {
        const label = count > 1 ? i18n.items : i18n.item;
        const installmentValue = formatMoney(Math.round(total / installments), currency);
        parcEl.textContent = `${count} ${label} · ${installments}x de ${installmentValue} ${i18n.noInterest}`;
      }
    }

    const addBtn = root.querySelector('[data-jv-look-add]');
    const addEmpty = root.querySelector('[data-jv-look-add-empty]');
    const addCount = root.querySelector('[data-jv-look-add-count]');

    if (addBtn instanceof HTMLButtonElement) {
      addBtn.disabled = !!ui.loading || count < 1;
      addBtn.setAttribute('aria-busy', ui.loading ? 'true' : 'false');
    }

    if (addEmpty instanceof HTMLElement) {
      addEmpty.hidden = count >= 1;
    }

    if (addCount instanceof HTMLElement) {
      addCount.hidden = count < 1;
      addCount.textContent = i18n.addCount.replace('__N__', String(count));
    }
  }

  /**
   * @param {object} item
   * @param {string} value
   */
  function selectSize(item, value) {
    if (!item || item.sizeOptionIndex == null) return;
    if (!isSizeAvailable(item, value)) return;

    item.selectedSize = String(value);
    const sizeIndex = item.sizeOptionIndex;
    const found = item.variants.find(
      (variant) => String(variant.options[sizeIndex]) === String(value)
    );

    if (!found) return;

    item.id = String(found.id);
    item.cents = found.price;
    item.available = !!found.available;

    if (!item.available && !item.locked) item.checked = false;
  }

  /**
   * @param {object[]} items
   * @param {object | null | undefined} variant
   */
  function syncMainFromPdp(items, variant) {
    if (!variant || !items[0] || !items[0].locked) return;

    const item = items[0];
    item.id = String(variant.id);
    item.cents = variant.price;
    item.available = !!variant.available;

    if (item.sizeOptionIndex != null && Array.isArray(variant.options)) {
      item.selectedSize = String(variant.options[item.sizeOptionIndex]);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {object[]} items
   * @param {HTMLButtonElement} button
   * @param {{ loading: boolean }} ui
   */
  async function handleAdd(root, items, button, ui) {
    const payload = [];

    for (const item of items) {
      if (item.checked && item.available) {
        payload.push({ id: parseInt(String(item.id), 10), quantity: 1 });
      }
    }

    if (!payload.length) return;

    ui.loading = true;
    render(root, items, ui);

    const theme = window.Theme || globalThis.Theme;
    const cartAddUrl = theme?.routes?.cart_add_url || '/cart/add.js';

    const sectionIds = [];

    for (const node of document.querySelectorAll('cart-items-component')) {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        sectionIds.push(node.dataset.sectionId);
      }
    }

    /** @type {{ items: { id: number; quantity: number }[]; sections?: string }} */
    const body = { items: payload };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    try {
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && !data.status) {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE, {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: root.dataset.sectionId || '',
              data: {
                source: 'jv-buy-the-look',
                itemCount: data.item_count,
              },
            },
          })
        );
      } else {
        const message = data.message || data.description || 'Não foi possível adicionar ao carrinho.';
        if (typeof window.alert === 'function') window.alert(message);
      }
    } catch {
      if (typeof window.alert === 'function') {
        window.alert('Não foi possível adicionar ao carrinho.');
      }
    } finally {
      ui.loading = false;
      render(root, items, ui);
      button.blur();
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initSection(root) {
    if (!root || root.dataset.jvLookInit === 'true') return;

    const itemsData = parseJson(root.querySelector('[data-jv-look-items]'), []);

    if (!Array.isArray(itemsData) || !itemsData.length) return;

    root.dataset.jvLookInit = 'true';

    /** @type {object[]} */
    const items = itemsData;
    const ui = { loading: false };
    const productId = root.dataset.productId || '';

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const toggle = target.closest('[data-jv-look-toggle]');

      if (toggle instanceof HTMLButtonElement && root.contains(toggle) && !toggle.disabled) {
        const row = toggle.closest('[data-jv-look-row]');
        if (!(row instanceof HTMLElement)) return;

        const index = Number(row.dataset.index);
        const item = items[index];

        if (!item || item.locked || !item.available) return;

        item.checked = !item.checked;
        render(root, items, ui);
        return;
      }

      const sizeBtn = target.closest('[data-jv-look-size]');

      if (sizeBtn instanceof HTMLElement && root.contains(sizeBtn)) {
        event.preventDefault();
        const row = sizeBtn.closest('[data-jv-look-row]');
        if (!(row instanceof HTMLElement)) return;

        const index = Number(row.dataset.index);
        const item = items[index];
        const value = sizeBtn.dataset.jvSize || '';

        if (!item || !value) return;

        selectSize(item, value);
        render(root, items, ui);
        return;
      }

      const addBtn = target.closest('[data-jv-look-add]');

      if (addBtn instanceof HTMLButtonElement && root.contains(addBtn) && !addBtn.disabled) {
        handleAdd(root, items, addBtn, ui);
      }
    });

    document.addEventListener(VARIANT_UPDATE, (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (!detail?.resource) return;

      const eventProductId = detail.data?.productId;
      if (eventProductId && productId && String(eventProductId) !== String(productId)) return;

      syncMainFromPdp(items, detail.resource);
      render(root, items, ui);
    });

    render(root, items, ui);
  }

  function init() {
    for (const root of document.querySelectorAll('[data-jv-look]')) {
      if (root instanceof HTMLElement) initSection(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {Event} */ (event).target;
    if (!(target instanceof Element)) return;

    const root = target.querySelector('[data-jv-look]');
    if (root instanceof HTMLElement) initSection(root);
  });
})();
